<template>
  <div class="contents-history">
    <div v-if="loading && requests.length === 0" class="history-grid">
      <div v-for="index in 6" :key="index">
        <b-skeleton-img no-aspect height="220px"></b-skeleton-img>
        <b-skeleton width="80%" class="mt-50"></b-skeleton>
        <b-skeleton width="50%"></b-skeleton>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="loaded && requests.length === 0" class="history-empty">
      <feather-icon icon="ImageIcon" size="36" class="text-muted mb-1"></feather-icon>
      <h5>{{ $t('ia.contents.emptyHistoryTitle') }}</h5>
      <p class="avenir-medium text-muted">{{ $t('ia.contents.emptyHistoryText') }}</p>
      <b-button class="generate-contents-ai" @click="$emit('new_request')">
        <feather-icon icon="PlusIcon" class="mr-25"></feather-icon>{{ $t('ia.contents.newRequest') }}
      </b-button>
    </div>

    <template v-else>
      <div class="history-grid">
        <div v-for="request in requests" :key="request.uuid" class="history-card" @click="$emit('open_request', request.uuid)">
          <div class="history-cover">
            <template v-if="request.cover">
              <video v-if="isVideo(request.cover)" :src="request.cover" class="history-cover-media" muted preload="metadata"></video>
              <img v-else :src="request.cover" class="history-cover-media" alt="" />
            </template>
            <div v-else class="history-cover-empty">
              <b-spinner v-if="isActive(request)" small></b-spinner>
              <feather-icon v-else icon="ImageIcon" size="24"></feather-icon>
            </div>
            <b-badge :variant="statusVariant(request.status)" class="history-status">{{ $t(`ia.contents.requestStatus.${request.status}`) }}</b-badge>
          </div>
          <div class="history-info">
            <p class="history-prompt avenir-medium">{{ request.prompt }}</p>
            <small class="text-muted d-block">{{ planSummary(request) }}</small>
            <small class="text-muted d-block">{{ formatDate(request.created_at) }}</small>
          </div>
        </div>
      </div>

      <div v-if="next_page" class="text-center mt-2">
        <b-button variant="outline-secondary" :disabled="loading" @click="getRequests(next_page)">
          <b-spinner v-if="loading" small class="mr-50"></b-spinner>{{ $t('ia.contents.loadMore') }}
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
  BSpinner,
  BSkeleton,
  BSkeletonImg,
} from "bootstrap-vue";
import { getContentRequests } from '@/services/ai-contents';
import { formatDateIA } from '@/libs/utils/times';
import { showToast } from '@/libs/utils/others';
import { ACTIVE_REQUEST_STATUSES, REQUEST_STATUS_VARIANTS } from './contents-utils';

export default {
  name: 'ContentsHistory',
  components: {
    BButton,
    BBadge,
    BSpinner,
    BSkeleton,
    BSkeletonImg,
  },
  props: {
    // { type: 'campaign' | 'casting_call', uuid } or null for standalone BrandMeAI
    source: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      requests: [],
      loading: false,
      loaded: false,
      next_page: null,
    };
  },
  created() {
    this.$root.$on('ai_content_updated', this.onContentUpdated);
    this.getRequests();
  },
  beforeDestroy() {
    this.$root.$off('ai_content_updated', this.onContentUpdated);
  },
  methods: {
    getRequests(page = 1) {
      const params = { page };
      if (this.source && this.source.type && this.source.uuid) params[this.source.type] = this.source.uuid;
      else params.standalone = true;

      this.loading = true;
      getContentRequests(params).then((response) => {
        const results = response.results || [];
        this.requests = page === 1 ? results : this.requests.concat(results);
        this.next_page = response.next ? page + 1 : null;
      }).catch(() => {
        showToast(this, 'danger', 'AlertCircleIcon', 'ia.contents.errorHistory');
      }).finally(() => {
        this.loading = false;
        this.loaded = true;
      });
    },
    onContentUpdated(event) {
      const request = event && this.requests.find((item) => item.uuid === event.request_uuid);
      if (!request) return;
      if (event.request_status) request.status = event.request_status;
      const asset = !event.is_intermediate && (event.assets || [])[0];
      if (!request.cover && asset) request.cover = asset.url;
    },
    isActive(request) {
      return ACTIVE_REQUEST_STATUSES.includes(request.status);
    },
    isVideo(url) {
      return /\.(mp4|mov|webm)(\?|$)/i.test(url);
    },
    statusVariant(status) {
      // Solid variants: the badge is shown over the cover image
      return (REQUEST_STATUS_VARIANTS[status] || 'secondary').replace('light-', '');
    },
    planSummary(request) {
      const totals = (request.plan || []).reduce((sum, row) => {
        sum[row.asset_type] = (sum[row.asset_type] || 0) + (parseInt(row.quantity) || 0);
        return sum;
      }, {});
      const parts = [];
      if (totals.image) parts.push(this.$tc('ia.contents.imagesCount', totals.image, { count: totals.image }));
      if (totals.video) parts.push(this.$tc('ia.contents.videosCount', totals.video, { count: totals.video }));
      if (request.credits_estimated !== undefined && request.credits_estimated !== null) {
        parts.push(`${request.credits_estimated} ${this.$t('ia.contents.creditsShort')}`);
      }
      return parts.join(' · ');
    },
    formatDate(date) {
      return date ? formatDateIA(new Date(date), this.$i18n.locale) : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}
.history-card {
  cursor: pointer;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #fff;
  transition: box-shadow 200ms ease, transform 200ms ease;

  &:hover {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.12);
    transform: translateY(-2px);
  }
}
.history-cover {
  position: relative;
  aspect-ratio: 4 / 5;
  background-color: #f3f2f7;

  .history-cover-media {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .history-cover-empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b9b9c3;
  }
  .history-status {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
  }
}
.history-info {
  padding: 0.75rem;

  .history-prompt {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 0.25rem;
  }
}
.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4rem 1rem;
}
.generate-contents-ai {
  border: 0;
  background: linear-gradient(to left bottom, rgb(124, 214, 175), rgb(62, 123, 160));
}
@media (max-width: 500px) {
  .history-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
}
</style>
